import { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryFn } from './queryFn';
import type { Portfolio } from '../usePortfolio';

type Args = {
  portfolioId: Portfolio['id'];
  startTimestamp: number;
  endTimestamp: number;
};

export const QUERY_KEY = 'PORTFOLIO_PERFORMANCE_TIMESERIES' as const;

export const getQueryKey = ({
  portfolioId,
  startTimestamp,
  endTimestamp,
}: Args) => [QUERY_KEY, portfolioId, { startTimestamp, endTimestamp }] as const;

const DEFAULT_SECONDS = 1500;
const DEFAULT_ATTEMPTS = 0;
const MAX_ATTEMPTS = 3;

const shouldRefetch = (
  obj: Record<string, number> | undefined,
  attempt: number
) => {
  if (typeof obj === 'undefined') return true;
  return Object.keys(obj).length === 0 && attempt <= MAX_ATTEMPTS;
};

export const usePortfolioPerformanceTimeseries = ({
  portfolioId,
  startTimestamp,
  endTimestamp,
}: Args) => {
  const [isRefetchingOnInterval, setIsRefetchingOnInterval] = useState(false);

  const attempt = useRef(DEFAULT_ATTEMPTS);
  const seconds = useRef(DEFAULT_SECONDS);

  useEffect(() => {
    attempt.current = DEFAULT_ATTEMPTS;
    seconds.current = DEFAULT_SECONDS;
  }, [portfolioId, startTimestamp]);

  const { data, status, dataUpdatedAt } = useQuery({
    queryKey: getQueryKey({ portfolioId, startTimestamp, endTimestamp }),
    queryFn,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const { data } = query.state;
      if (typeof data === 'undefined') return DEFAULT_SECONDS;
      /**
       * If no data was returned, refetch on the interval
       */
      if (shouldRefetch(data as Record<string, number>, attempt.current)) {
        setIsRefetchingOnInterval(true);
        return seconds.current;
      }
      return Infinity;
    },
  });

  useEffect(() => {
    /** mimic onSettled callback from useQuery v4 */
    if (status === 'success' || status === 'error') {
      if (
        typeof data !== 'undefined' &&
        shouldRefetch(data as Record<string, number>, attempt.current)
      ) {
        /**
         * If no data is returned, increment attempt and increase polling interval
         */
        attempt.current += 1;
        /**
         * Exponential backoff (MAX_ATTEMPTS is 3)
         * 1500 * 1 = 1500ms
         * 1500 * 2 = 3000ms
         * 3000 * 3 = 9000ms
         */
        seconds.current = attempt.current * seconds.current;
      } else {
        setIsRefetchingOnInterval(false);
      }
    }
  }, [data, status, dataUpdatedAt]);

  useEffect(() => {
    if (status === 'error') {
      /**
       * If there is an error, stop polling
       */
      attempt.current = MAX_ATTEMPTS;
      setIsRefetchingOnInterval(false);
    }
  }, [status]);

  return {
    data,
    status: isRefetchingOnInterval ? 'pending' : status,
  };
};
